import { useTranslation } from "i18n";
import {
  AlertMessage,
  Popup,
  useGetSubscriptions,
  SubscriptionTypes,
  PlanCodePeriod,
  SubscriptionTimePeriod,
} from "component-library";

import ReinstateOriginalPlanModalHeader from "./ReinstateOriginalPlanModalHeader";
import ReinstateOriginalPlanModalContent from "./ReinstateOriginalPlanModalContent";
import ReinstateOriginalPlanModalSubscriptionBatch from "./ReinstateOriginalPlanModalSubscriptionBatch";

import "./ReinstateOriginalPlanModal.scss";

const DEFAULT_PRICE = "0.00";

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  planCode: string;
  onClosePopup: () => void;
  onSuccessCallback: () => void;
}

const ReinstateOriginalPlanModal = ({
  isOpen,
  isLoading,
  planCode,
  onClosePopup,
  onSuccessCallback,
}: Props) => {
  const { t } = useTranslation();

  const { data: subscriptions } = useGetSubscriptions();
  const annualSubscription = subscriptions?.find((sub) =>
    sub.code.includes(PlanCodePeriod.Annual)
  );
  const monthlySubscription = subscriptions?.find((sub) =>
    sub.code.includes(PlanCodePeriod.Monthly)
  );

  const monthlySubscriptionPlanCodes =
    subscriptions
      ?.filter((sub) => sub.code.includes(PlanCodePeriod.Monthly))
      .map((sub) => sub.code.toString()) || [];

  const isFromAnnualPlanToMonthly =
    monthlySubscriptionPlanCodes.includes(planCode);

  const fromPlan = isFromAnnualPlanToMonthly
    ? SubscriptionTypes.Spotlight_Performer_Annual
    : SubscriptionTypes.Spotlight_Performer_Monthly;
  const toPlan = isFromAnnualPlanToMonthly
    ? SubscriptionTypes.Spotlight_Performer_Monthly
    : SubscriptionTypes.Spotlight_Performer_Annual;

  const subscriptionTimePeriod = isFromAnnualPlanToMonthly
    ? SubscriptionTimePeriod.Yearly
    : SubscriptionTimePeriod.Monthly;

  const priceWithoutTax = isFromAnnualPlanToMonthly
    ? annualSubscription?.amount
    : monthlySubscription?.amount;

  const priceWithTax = isFromAnnualPlanToMonthly
    ? annualSubscription?.total
    : monthlySubscription?.total;

  const handleClose = () => {
    if (!isLoading) {
      onClosePopup();
    }
  };

  const handleReinstateOriginalPlanModal = () => {
    onSuccessCallback();
    handleClose();
  };

  return (
    <>
      {isOpen && (
        <Popup
          width={{ lg: 6, md: 6 }}
          texts={{
            closePopup: "",
          }}
          close={handleClose}
          className="z-50"
        >
          <AlertMessage
            texts={{
              title: t(
                "performer:accountSettings.mySubscriptionPage.reinstateOriginalPlanModal.title"
              ),
            }}
            className="reinstate-original-plan-modal"
            buttons={[
              {
                name: t(
                  "performer:accountSettings.mySubscriptionPage.reinstateOriginalPlanModal.button.submit"
                ),
                click: handleReinstateOriginalPlanModal,
                type: "primary",
                ctaDisabled: isLoading,
              },
              {
                name: t(
                  "performer:accountSettings.mySubscriptionPage.reinstateOriginalPlanModal.button.close"
                ),
                click: handleClose,
                type: "secondary",
                ctaDisabled: isLoading,
              },
            ]}
          >
            <ReinstateOriginalPlanModalHeader
              fromPlan={fromPlan}
              toPlan={toPlan}
            />
            <div className="mb-[30px]">
              <ReinstateOriginalPlanModalContent />
              <ReinstateOriginalPlanModalSubscriptionBatch
                fromPlan={fromPlan}
                priceWithTax={priceWithTax ?? DEFAULT_PRICE}
                priceWithoutTax={priceWithoutTax ?? DEFAULT_PRICE}
                subscriptionTimePeriod={subscriptionTimePeriod}
              />
            </div>
          </AlertMessage>
        </Popup>
      )}
    </>
  );
};

export default ReinstateOriginalPlanModal;
